import { type Theme, alpha, type Components } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { typographyClasses } from '@mui/material/Typography';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { chipClasses } from '@mui/material/Chip';
import { iconButtonClasses } from '@mui/material/IconButton';

import { IconChevronDown } from '@/components/icon/tabler/IconChevronDown';
import { IconChevronLeft } from '@/components/icon/tabler/IconChevronLeft';
import { IconChevronRight } from '@/components/icon/tabler/IconChevronRight';
import { IconChevronsLeft } from '@/components/icon/tabler/IconChevronsLeft';
import { IconChevronsRight } from '@/components/icon/tabler/IconChevronsRight';
import { IconX } from '@/components/icon/tabler/IconX';
import {
  gray,
  red,
  green,
  purple,
  orange,
  yellow,
  brand,
  surfaceTokens,
  controlTokens,
} from '@/theme/themePrimitives';

const chipFeedbackColors = [
  {
    color: 'success',
    fill: controlTokens.successHover,
    hover: controlTokens.successPressed,
    foreground: surfaceTokens.paper,
    soft: controlTokens.successSoft,
    outline: controlTokens.successHover,
  },
  {
    color: 'warning',
    fill: controlTokens.warningFill,
    hover: controlTokens.warningFillHover,
    foreground: controlTokens.primary,
    soft: controlTokens.warningSoft,
    outline: controlTokens.warning,
  },
  {
    color: 'error',
    fill: controlTokens.error,
    hover: controlTokens.errorHover,
    foreground: surfaceTokens.paper,
    soft: controlTokens.errorSoft,
    outline: controlTokens.error,
  },
  {
    color: 'info',
    fill: controlTokens.infoFill,
    hover: controlTokens.infoFillHover,
    foreground: controlTokens.primary,
    soft: controlTokens.infoSoft,
    outline: controlTokens.info,
  },
  {
    color: 'golden',
    fill: yellow[400],
    hover: yellow[300],
    foreground: controlTokens.primary,
    soft: yellow[100],
    outline: yellow[700],
  },
  {
    color: 'whatsapp',
    fill: controlTokens.whatsapp,
    hover: controlTokens.whatsapp,
    foreground: controlTokens.primary,
    soft: green[100],
    outline: green[600],
  },
] as const;

export const dataDisplayCustomizations: Components<Theme> = {
  MuiTable: {
    styleOverrides: {
      root: ({ theme }) => ({ backgroundColor: (theme.vars || theme).palette.background.paper }),
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: surfaceTokens.subtle,
        '& .MuiTableCell-head': {
          fontFamily: theme.typography.subtitle2.fontFamily,
          fontWeight: 600,
          fontSize: 12,
          textTransform: 'none',
        },
        ...theme.applyStyles('dark', {
          backgroundColor: (theme.vars || theme).palette.background.paper,
        }),
      }),
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiTableRow-hover:hover': { backgroundColor: surfaceTokens.subtle },
        '&.Mui-selected': {
          backgroundColor: (theme.vars || theme).palette.action.selected,
          '& > .MuiTableCell-root:first-of-type': {
            boxShadow: `inset 3px 0 0 ${surfaceTokens.selectionAccent}`,
          },
        },
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: { fontVariantNumeric: 'tabular-nums' },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: '1rem',
          height: '1rem',
          color: (theme.vars || theme).palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: 'flex',
          gap: 8,
          padding: '2px 8px',
          borderRadius: (theme.vars || theme).shape.borderRadius,
          '&.Mui-selected': {
            backgroundColor: (theme.vars || theme).palette.action.selected,
            [`& .${svgIconClasses.root}`]: {
              color: (theme.vars || theme).palette.text.primary,
            },
            '&:focus-visible': {
              backgroundColor: (theme.vars || theme).palette.action.selected,
            },
            '&:hover': {
              backgroundColor: surfaceTokens.interactivePressed,
            },
          },
          '&:focus-visible': {
            outline: `2px solid ${surfaceTokens.selectionAccent}`,
            outlineOffset: -2,
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        padding: '4px 8px',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: {
        border: `1px solid ${surfaceTokens.borderSubtle}`,
        '&.MuiAvatar-colorDefault': {
          backgroundColor: brand[100],
          color: controlTokens.primary,
        },
      },
    },
  },
  MuiBadge: {
    styleOverrides: {
      badge: {
        // Signal 06: count is Coral with Dark Blue text; the dot is Vivid Red. Both sit on a 2px white ring.
        height: 20,
        minWidth: 20,
        padding: '0 6px',
        fontWeight: 700,
        fontSize: 11,
        lineHeight: '20px',
        borderRadius: 999,
        boxShadow: `0 0 0 2px ${surfaceTokens.paper}`,
        '&.MuiBadge-dot': {
          height: 8,
          minWidth: 8,
          padding: 0,
          borderRadius: '50%',
        },
        variants: [
          {
            props: { color: 'default' },
            style: {
              backgroundColor: controlTokens.coral,
              color: controlTokens.primary,
            },
          },
          {
            props: { color: 'default', variant: 'dot' },
            style: {
              backgroundColor: controlTokens.vividRed,
            },
          },
        ],
      },
    },
  },
  MuiChip: {
    defaultProps: { deleteIcon: <IconX />, size: 'small' },
    styleOverrides: {
      root: ({ theme }) => ({
        border: '1px solid',
        borderRadius: '999px',
        '&.Mui-focusVisible': { boxShadow: controlTokens.focusRing },
        [`& .${chipClasses.label}`]: {
          fontWeight: 600,
        },
        [`& .${chipClasses.avatarSmall}`]: {
          width: 16,
          height: 16,
          fontSize: '0.5rem',
          marginLeft: 0,
          marginRight: 4,
        },
        [`& .${chipClasses.avatarMedium}`]: {
          marginLeft: 0,
          marginRight: 6,
        },
        variants: [
          {
            props: {
              color: 'default',
            },
            style: {
              borderColor: gray[200],
              backgroundColor: 'white',
              [`& .${chipClasses.label}`]: {
                color: gray[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: gray[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: gray[700],
                backgroundColor: gray[800],
                [`& .${chipClasses.label}`]: {
                  color: gray[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: gray[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'success',
            },
            style: {
              borderColor: green[200],
              backgroundColor: green[400],
              [`& .${chipClasses.label}`]: {
                color: green[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: green[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: green[800],
                backgroundColor: green[900],
                [`& .${chipClasses.label}`]: {
                  color: green[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: 'white',
                },
              }),
            },
          },
          {
            props: {
              color: 'success',
              variant: 'outlined',
            },
            style: {
              borderColor: green[400],
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: green[100],
              },
              [`& .${chipClasses.label}`]: {
                color: green[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: green[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: green[600],
                [`& .${chipClasses.label}`]: {
                  color: green[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: green[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'warning',
            },
            style: {
              borderColor: orange[300],
              backgroundColor: orange[300],
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: `${orange[500]} !important`,
              },
            },
          },
          {
            props: {
              color: 'warning',
              variant: 'outlined',
            },
            style: {
              borderColor: orange[500],
              backgroundColor: 'transparent',
              [`& .${chipClasses.label}`]: {
                color: orange[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: orange[600],
              },
              '&:hover': {
                backgroundColor: orange[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: orange[400],
                [`& .${chipClasses.label}`]: {
                  color: orange[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: orange[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'success',
              variant: 'filled',
            },
            style: {
              backgroundColor: green[400],
              borderColor: green[400],
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
            },
          },
          {
            props: {
              color: 'error',
            },
            style: {
              borderColor: red[400],
              backgroundColor: red[400],
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
              [`& .${chipClasses.icon}`]: {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: alpha(red[400], 0.9),
                borderColor: red[400],
              },
              ...theme.applyStyles('dark', {
                borderColor: red[400],
                backgroundColor: red[400],
                [`& .${chipClasses.label}`]: {
                  color: 'white',
                },
                [`& .${chipClasses.icon}`]: {
                  color: 'white',
                },
              }),
            },
          },
          {
            props: {
              color: 'error',
              variant: 'outlined',
            },
            style: {
              borderColor: red[500],
              backgroundColor: 'transparent',
              [`& .${chipClasses.label}`]: {
                color: red[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: red[500],
              },
              '&:hover': {
                backgroundColor: alpha(red[400], 0.18),
              },
              ...theme.applyStyles('dark', {
                borderColor: red[400],
                [`& .${chipClasses.label}`]: {
                  color: red[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: red[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'ai',
            },
            style: {
              borderColor: purple[300],
              backgroundColor: purple[500],
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
              [`& .${chipClasses.icon}`]: {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: alpha(purple[400], 0.9),
                borderColor: purple[400],
              },
            },
          },
          {
            props: {
              color: 'ai',
              variant: 'outlined',
            },
            style: {
              borderColor: purple[700],
              backgroundColor: 'transparent',
              [`& .${chipClasses.label}`]: {
                color: purple[700],
              },
              [`& .${chipClasses.icon}`]: {
                color: purple[700],
              },
              '&:hover': {
                backgroundColor: alpha(purple[400], 0.18),
              },
              ...theme.applyStyles('dark', {
                borderColor: purple[500],
                [`& .${chipClasses.label}`]: {
                  color: purple[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: purple[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'info',
              variant: 'outlined',
            },
            style: {
              borderColor: brand[500],
              backgroundColor: 'transparent',
              [`& .${chipClasses.label}`]: {
                color: brand[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: brand[500],
              },
              '&:hover': {
                backgroundColor: alpha(brand[400], 0.18),
              },
              ...theme.applyStyles('dark', {
                borderColor: brand[500],
                [`& .${chipClasses.label}`]: {
                  color: brand[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: brand[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'golden',
            },
            style: {
              borderColor: yellow[400],
              backgroundColor: yellow[400],
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
              [`& .${chipClasses.icon}`]: {
                color: 'white',
              },
              '&:hover': {
                backgroundColor: alpha(yellow[500], 0.9),
                borderColor: yellow[500],
              },
            },
          },
          {
            props: {
              color: 'golden',
              variant: 'outlined',
            },
            style: {
              borderColor: yellow[600],
              backgroundColor: 'transparent',
              [`& .${chipClasses.label}`]: {
                color: yellow[600],
              },
              [`& .${chipClasses.icon}`]: {
                color: yellow[600],
              },
              '&:hover': {
                backgroundColor: alpha(yellow[400], 0.08),
              },
              ...theme.applyStyles('dark', {
                borderColor: yellow[600],
                [`& .${chipClasses.label}`]: {
                  color: yellow[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: yellow[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'whatsapp',
            },
            style: {
              backgroundColor: '#25D366',
              borderColor: '#E2FDD3',
              [`& .${chipClasses.label}`]: {
                color: 'white',
              },
              [`& .${chipClasses.icon}`]: {
                color: 'white',
              },
            },
          },
          {
            props: { size: 'xsmall' },
            style: {
              height: 18,
              minHeight: 18,
              [`& .${chipClasses.label}`]: {
                fontSize: '10px',
                lineHeight: 1,
                paddingLeft: 4,
                paddingRight: 4,
              },
              [`& .${chipClasses.icon}`]: {
                marginLeft: 2,
                marginRight: 2,
                fontSize: '10px',
              },
              [`& .${chipClasses.deleteIcon}`]: {
                fontSize: '10px',
                marginRight: 2,
                marginLeft: 2,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: '10px',
              },
            },
          },
          {
            props: { size: 'small' },
            style: {
              height: 22,
              minHeight: 22,
              [`& .${chipClasses.label}`]: {
                fontSize: '10.5px',
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: '10.5px',
              },
              [`&:has(.${chipClasses.avatar})`]: {
                paddingLeft: 4,
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              height: 26,
              minHeight: 26,
              [`& .${chipClasses.label}`]: {
                fontSize: '11.5px',
              },
              [`&:has(.${chipClasses.avatar})`]: {
                paddingLeft: 6,
              },
            },
          },
          {
            props: { size: 'large' },
            style: {
              height: 30,
              minHeight: 30,
              [`&:has(.${chipClasses.avatar})`]: {
                paddingLeft: 4,
              },
              [`& .${chipClasses.avatar}`]: {
                marginLeft: 0,
                marginRight: 6,
                width: 24,
                height: 24,
                fontSize: theme.typography.pxToRem(11),
              },
              [`& .${chipClasses.label}`]: {
                fontSize: 12.5,
                lineHeight: 1.35,
                paddingLeft: theme.spacing(0.5),
                paddingRight: theme.spacing(1.25),
              },
              [`& .${chipClasses.icon}`]: {
                fontSize: 17,
                marginLeft: 6,
                marginRight: 8,
              },
              [`& .${chipClasses.deleteIcon}`]: {
                fontSize: 17,
                marginRight: 8,
                marginLeft: 8,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: theme.typography.body1.fontSize,
              },
            },
          },
          ...chipFeedbackColors.flatMap(({ color, fill, hover, foreground, soft, outline }) => [
            {
              props: { color, variant: 'filled' as const },
              style: {
                backgroundColor: fill,
                borderColor: fill,
                color: foreground,
                [`& .${chipClasses.label}, & .${chipClasses.icon}`]: { color: foreground },
                '&:hover': { backgroundColor: hover, borderColor: hover },
              },
            },
            {
              props: { color, variant: 'outlined' as const },
              style: {
                backgroundColor: surfaceTokens.paper,
                borderColor: outline,
                color: outline,
                [`& .${chipClasses.label}, & .${chipClasses.icon}`]: { color: outline },
                '&:hover': { backgroundColor: soft },
              },
            },
          ]),
        ],
      }),
    },
    variants: [
      {
        props: { color: 'active' },
        style: {
          backgroundColor: controlTokens.primary,
          borderColor: controlTokens.primary,
          color: gray[50],
          [`& .${chipClasses.label}`]: { color: gray[50] },
          [`& .${chipClasses.icon}`]: { color: gray[50] },
          '&:hover': {
            backgroundColor: controlTokens.primaryHover,
            borderColor: controlTokens.primaryHover,
          },
        },
      },
    ],
  },
  MuiTablePagination: {
    defaultProps: {
      slots: {
        actions: {
          firstButtonIcon: IconChevronsLeft,
          previousButtonIcon: IconChevronLeft,
          nextButtonIcon: IconChevronRight,
          lastButtonIcon: IconChevronsRight,
        },
      },
    },
    styleOverrides: {
      toolbar: {
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        columnGap: 8,
        rowGap: 0,
        minHeight: 52,
      },
      actions: ({ theme }) => ({
        display: 'flex',
        gap: 4,
        marginRight: 0,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
          [`&:not(.${iconButtonClasses.disabled})`]: { color: brand[900] },
        },
        ...theme.applyStyles('dark', {
          [`& .${iconButtonClasses.root}:not(.${iconButtonClasses.disabled})`]: {
            color: brand[100],
          },
        }),
      }),
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: 'small',
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: 'small',
            },
            style: {
              fontSize: '1rem',
            },
          },
        ],
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiTypography-h1, &.MuiTypography-h2, &.MuiTypography-h3, &.MuiTypography-h4, &.MuiTypography-h5, &.MuiTypography-h6':
          {
            color: surfaceTokens.dark,
            ...theme.applyStyles('dark', { color: (theme.vars || theme).palette.text.primary }),
          },
      }),
    },
    variants: [
      {
        props: { component: 'h2' },
        style: ({ theme }) => ({
          fontSize: theme.typography.pxToRem(28),
          fontFamily: theme.typography.h1.fontFamily,
          fontWeight: 600,
          lineHeight: 1.15,
          letterSpacing: '-0.025em',
          [`&.${typographyClasses.noWrap}`]: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          [theme.breakpoints.down('md')]: {
            fontSize: theme.typography.pxToRem(22),
          },
        }),
      },
    ],
  },
  MuiTextField: {
    defaultProps: {
      size: 'medium',
    },
    variants: [
      {
        props: { multiline: true },
        style: {
          '.MuiInputBase-root': { height: 'auto' },
        },
      },
    ],
    styleOverrides: {
      root: {
        '& .MuiButtonBase-root': {
          borderRadius: '50%',
          border: 0,
          width: 26,
          height: 26,
        },
      },
    },
  },
  MuiAutocomplete: {
    defaultProps: {
      popupIcon: <IconChevronDown />,
      clearIcon: <IconX />,
      size: 'small',
    },
    styleOverrides: {
      popper: ({ theme }) => ({
        '& .MuiPaper-root': {
          backgroundColor: (theme.vars || theme).palette.background.paper,
          backgroundImage: 'none',
          color: (theme.vars || theme).palette.text.primary,
          marginTop: '4px !important',
          marginBottom: '4px !important',
          border: `1px solid ${(theme.vars || theme).palette.divider}`,
          boxShadow: surfaceTokens.shadowOverlay,
          borderRadius: 2,
          '& .MuiAutocomplete-listbox': { padding: '4px 0' },
          '& .MuiAutocomplete-option': {
            minHeight: 40,
            margin: '2px 6px',
            borderRadius: 1,
            '&:hover, &.Mui-focused': {
              backgroundColor: (theme.vars || theme).palette.action.hover,
            },
            '&[aria-selected="true"]': {
              backgroundColor: (theme.vars || theme).palette.action.selected,
              color: controlTokens.primary,
              fontWeight: 600,
            },
            '&[aria-selected="true"]:hover, &[aria-selected="true"].Mui-focused': {
              backgroundColor: (theme.vars || theme).palette.action.selected,
            },
          },
        },
      }),
      root: ({ ownerState }) => ({
        '& .MuiButtonBase-root': {
          borderRadius: '50%',
          border: 0,
          width: 26,
          height: 26,
        },
        '& span.MuiAutocomplete-tag': {
          display: 'inline-flex',
          alignItems: 'center',
          alignSelf: 'center',
          ml: 0.75,
          mr: 0.5,
          lineHeight: 1.25,
        },
        ...(ownerState.multiple && {
          '& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
            height: 'auto',
            minHeight: 32,
          },
        }),
      }),
    },
  },
};
